import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { colors, text, space, radius, sx } from '../design/tokens'
import { fmtFechaHora } from '../utils/fecha'
import Boton from './Boton'

// Tablero de Logística → del pago del cliente a la entrega (o al aviso de
// retiro), por transporte. El cálculo vive en TiemposEntregaService.

// Un color por tramo, del más rápido (verde) al más lento (rojo)
const COLOR_TRAMO = {
  h24: '#16a34a',
  h48: '#65a30d',
  h72: '#d97706',
  d5: '#ea580c',
  mas: '#dc2626',
}

// < 48 h en horas; de ahí en adelante en días (con un decimal)
export function fmtDuracion(h) {
  if (h === null || h === undefined) return '—'
  if (h < 1) return `${Math.round(h * 60)} min`
  if (h < 48) return `${Math.round(h)} h`
  return `${(h / 24).toFixed(1).replace('.', ',')} días`
}

function BarraTramos({ tramos, defs, total }) {
  if (!total) return <div style={st.barraVacia} />
  return (
    <div style={st.barra}>
      {defs.map(t => {
        const n = tramos[t.clave] || 0
        if (!n) return null
        return (
          <div key={t.clave} title={`${t.label}: ${n} (${Math.round(n / total * 100)}%)`}
            style={{ width: `${n / total * 100}%`, background: COLOR_TRAMO[t.clave] }} />
        )
      })}
    </div>
  )
}

function GrupoCard({ g, defs, activo, onClick }) {
  const fuera = g.sin_pago + g.pago_posterior
  return (
    <button type="button" onClick={onClick} disabled={!g.medidos}
      style={{ ...st.grupo, ...(activo ? st.grupoActivo : {}), cursor: g.medidos ? 'pointer' : 'default' }}
      title={g.medidos ? 'Ver las entregas más lentas' : undefined}>
      <div style={st.grupoLabel}>{g.label}</div>
      <div style={st.grupoNum}>{fmtDuracion(g.mediana_h)}</div>
      <div style={st.grupoSub}>
        {g.medidos
          ? <>mediana · promedio {fmtDuracion(g.promedio_h)}<br />9 de cada 10 en {fmtDuracion(g.p90_h)} o menos</>
          : 'sin entregas medibles'}
      </div>
      <BarraTramos tramos={g.tramos} defs={defs} total={g.medidos} />
      <div style={st.grupoPie}>
        <span style={sx.mono}>{g.medidos}</span> {g.medidos === 1 ? 'entrega medida' : 'entregas medidas'}
        {fuera > 0 && (
          <span style={{ color: colors.textMeta }} title="No se pueden medir: la venta no tiene pago en LGR (cuenta corriente, remito libre) o el pago se registró después de entregar (cobro contra entrega, carga tardía)">
            {' '}· {fuera} sin medir
          </span>
        )}
      </div>
    </button>
  )
}

export default function TiemposEntregaCard({ data }) {
  const navigate = useNavigate()
  const [abierto, setAbierto] = useState(null)

  if (!data) return null
  const defs = data.tramos
  const grupoAbierto = data.grupos.find(g => g.clave === abierto)
  // En Andreani y retiros la columna Fletero no dice nada nuevo
  const conFletero = ['interno', 'externo'].includes(grupoAbierto?.clave)

  return (
    <div style={st.card}>
      <h3 style={st.titulo}>⏱ Tiempos de entrega — del pago a la entrega</h3>
      <p style={st.bajada}>
        Desde que el cliente paga hasta que el fletero marca la entrega. En los retiros por sucursal, hasta el aviso de que el pedido ya está disponible.
      </p>

      <div style={st.grilla}>
        {data.grupos.map(g => (
          <GrupoCard key={g.clave} g={g} defs={defs} activo={abierto === g.clave}
            onClick={() => setAbierto(abierto === g.clave ? null : g.clave)} />
        ))}
      </div>

      <div style={st.leyenda}>
        {defs.map(t => (
          <span key={t.clave} style={st.leyItem}>
            <i style={{ ...st.leyPunto, background: COLOR_TRAMO[t.clave] }} />{t.label}
          </span>
        ))}
      </div>

      {grupoAbierto && (
        <div style={{ marginTop: space.xl }}>
          <div style={st.subtitulo}>Las más lentas — {grupoAbierto.label}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>Documento</th>
                  <th style={sx.th}>Venta</th>
                  <th style={sx.th}>Cliente</th>
                  {conFletero && <th style={sx.th}>Fletero</th>}
                  <th style={sx.th}>Pagó</th>
                  <th style={sx.th}>{grupoAbierto.clave === 'retiro' ? 'Aviso disponible' : 'Entregado'}</th>
                  <th style={{ ...sx.th, textAlign: 'right' }}>Tiempo</th>
                  <th style={sx.th}></th>
                </tr>
              </thead>
              <tbody>
                {grupoAbierto.mas_lentos.map(f => {
                  const ruta = f.entidad_tipo === 'nota_envio'
                    ? `/logistica/notas-envio/${f.entidad_id}`
                    : `/logistica/remitos/${f.entidad_id}`
                  return (
                    <tr key={`${f.entidad_tipo}-${f.entidad_id}`} style={st.tr}>
                      <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>
                        {f.numero || '—'} <span style={st.tipo}>{f.entidad_tipo === 'nota_envio' ? 'NE' : 'Re'}</span>
                      </td>
                      <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{f.nv_numero || '—'}</td>
                      <td style={sx.td}>{f.cliente || '—'}</td>
                      {conFletero && <td style={sx.td}>{f.fletero}</td>}
                      <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{fmtFechaHora(f.pagado_at)}</td>
                      <td style={{ ...sx.td, ...sx.mono, whiteSpace: 'nowrap' }}>{fmtFechaHora(f.fin_at)}</td>
                      <td style={{ ...sx.td, ...sx.mono, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtDuracion(f.horas)}</td>
                      <td style={{ ...sx.td, textAlign: 'right' }}>
                        <Boton variante="secondary" sm onClick={() => navigate(ruta)}>Ver →</Boton>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p style={st.nota}>
        Se mide cada documento entregado (un remito, o cada nota de envío en un T80); el período es el de la fecha de entrega. Si la venta tuvo varios pagos, cuenta el último.
        En los cobros online la hora es la del pago; en los cargados a mano, la hora en que el vendedor lo registró.
        “Sin medir” = la venta no tiene pago en LGR o el pago se registró después de entregar.
        En Andreani la entrega se registra cuando LGR la lee de sus trazas (cada 30 min).
      </p>
    </div>
  )
}

const st = {
  card: { background: colors.bg, borderRadius: radius.lg, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,.06)', marginBottom: '1rem' },
  titulo: { margin: 0, fontSize: '0.9375rem', fontWeight: 700, color: colors.textStrong },
  bajada: { margin: `${space.xs}px 0 ${space.xl}px`, fontSize: text.sec, color: colors.textSec },
  grilla: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: space.lg },
  grupo: {
    textAlign: 'left', font: 'inherit', background: colors.bg, border: `1px solid ${colors.border}`,
    borderRadius: radius.lg, padding: space.xl, display: 'flex', flexDirection: 'column', gap: space.sm,
  },
  grupoActivo: { borderColor: colors.primary, boxShadow: `0 0 0 1px ${colors.primary}` },
  grupoLabel: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  grupoNum: { fontSize: text.display, fontWeight: 800, color: colors.text, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  grupoSub: { fontSize: text.sec, color: colors.textSec, lineHeight: 1.4, minHeight: 34 },
  grupoPie: { fontSize: text.sec, color: colors.textSub },
  barra: { display: 'flex', height: 10, borderRadius: radius.xs, overflow: 'hidden', background: colors.borderSoft },
  barraVacia: { height: 10, borderRadius: radius.xs, background: colors.borderSoft },
  leyenda: { display: 'flex', gap: space.lg, flexWrap: 'wrap', marginTop: space.lg },
  leyItem: { display: 'flex', alignItems: 'center', gap: 5, fontSize: text.sec, color: colors.textSub, fontWeight: 600 },
  leyPunto: { width: 10, height: 10, borderRadius: 2, display: 'inline-block' },
  subtitulo: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: space.md },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, height: 44 },
  tipo: { fontSize: 10, fontWeight: 800, color: colors.t80Text, background: colors.t80Bg, padding: '1px 5px', borderRadius: radius.xs, marginLeft: 4 },
  nota: { fontSize: text.meta, color: colors.textMeta, margin: `${space.lg}px 0 0`, lineHeight: 1.5 },
}
